import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getAttendanceByDate,
  getMyWorkMode,
  sendWebClockEvent,
} from '../hrms/api/attendance';
import { setAttendancePresence } from '../api/away';
import {
  AttendanceClockAction,
  AttendanceClockRow,
  deriveAttendanceClockState,
  getAttendanceUserId,
  getAttendanceUserIds,
  localDayOffset,
  localToday,
} from '../utils/attendanceClock';
import {
  emitAttendanceClockUpdated,
  subscribeAttendanceClockUpdated,
} from '../utils/attendanceClockEvents';
import { isElevated } from '../hrms/utils/rolePermissions';

let attendanceClockSourceCounter = 0;

export function useAttendanceClock(user: any) {
  const sourceIdRef = useRef('');
  if (!sourceIdRef.current) {
    attendanceClockSourceCounter += 1;
    sourceIdRef.current = `attendance-clock-${attendanceClockSourceCounter}`;
  }
  const [nowTick, setNowTick] = useState(() => Date.now());
  const today = useMemo(() => localToday(new Date(nowTick)), [nowTick]);
  const previousDay = useMemo(() => localDayOffset(-1, new Date(nowTick)), [nowTick]);
  const userId = useMemo(() => getAttendanceUserId(user), [user]);
  const attendanceUserIds = useMemo(() => getAttendanceUserIds(user), [user]);
  const [rows, setRows] = useState<AttendanceClockRow[]>([]);
  const [carryoverRows, setCarryoverRows] = useState<AttendanceClockRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [loadedAttendanceUserId, setLoadedAttendanceUserId] = useState<string | null>(null);
  const [attendanceRevision, setAttendanceRevision] = useState(0);
  const [modeLoading, setModeLoading] = useState(true);
  const [busy, setBusy] = useState<AttendanceClockAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workfromhome, setWorkfromhome] = useState(false);
  const [modeOfWorking, setModeOfWorking] = useState<string | null>(null);
  const [isOvertime, setIsOvertime] = useState(false);
  const [overtimeFrom, setOvertimeFrom] = useState<string | null>(null);
  const [overtimeUntil, setOvertimeUntil] = useState<string | null>(null);
  const lastPublishedPresenceRef = useRef<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setModeLoading(true);
    getMyWorkMode()
      .then((mode) => {
        if (cancelled) return;
        setWorkfromhome(!!mode.workfromhome);
        setModeOfWorking(mode.mode_of_working);
        setIsOvertime(!!mode.is_overtime);
        setOvertimeFrom(mode.overtime_clock_from ?? null);
        setOvertimeUntil(mode.overtime_clock_until ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setWorkfromhome(false);
          setModeOfWorking(null);
          setIsOvertime(false);
          setOvertimeFrom(null);
          setOvertimeUntil(null);
        }
      })
      .finally(() => {
        if (!cancelled) setModeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isRemote = (modeOfWorking || '').trim().toLowerCase() === 'remote';
  const isOvertimeActive = !!(
    isOvertime &&
    overtimeUntil &&
    (!overtimeFrom || nowTick >= new Date(overtimeFrom).getTime()) &&
    nowTick < new Date(overtimeUntil).getTime()
  );
  const sessionClock = isRemote || isOvertimeActive || isElevated(user?.role);

  const refresh = useCallback(async () => {
    if (!userId) {
      setRows([]);
      setCarryoverRows([]);
      setLoadedAttendanceUserId(null);
      setRowsLoading(false);
      return;
    }

    setRowsLoading(true);
    try {
      const [todayData, previousDayData] = await Promise.all([
        getAttendanceByDate(today),
        getAttendanceByDate(previousDay),
      ]);
      setRows(Array.isArray(todayData) ? todayData : []);
      setCarryoverRows(Array.isArray(previousDayData) ? previousDayData : []);
      setLoadedAttendanceUserId(userId);
      setAttendanceRevision((current) => current + 1);
      setError(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't load attendance state.");
    } finally {
      setRowsLoading(false);
    }
  }, [previousDay, today, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeAttendanceClockUpdated((detail) => {
      if (detail.sourceId === sourceIdRef.current) return;
      if (detail.userId && !attendanceUserIds.includes(String(detail.userId))) return;
      refresh();
    });
  }, [attendanceUserIds, refresh]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const clock = useCallback(
    async (action: AttendanceClockAction) => {
      setBusy(action);
      try {
        await sendWebClockEvent(action === 'in', {
          workfromhome,
          overtime: isOvertimeActive,
        });
        try {
          await setAttendancePresence(action === 'in');
        } catch {
          // Attendance succeeded; chat presence will self-correct on the next
          // successful bridge call or snapshot refresh.
        }
        await refresh();
        emitAttendanceClockUpdated({
          action,
          clockedIn: action === 'in',
          userId: userId || undefined,
          sourceId: sourceIdRef.current,
        });
        setError(null);
      } catch (err: any) {
        const message = err?.message || `Couldn't clock ${action}.`;
        setError(message);
        throw err;
      } finally {
        setBusy(null);
      }
    },
    [isOvertimeActive, refresh, userId, workfromhome],
  );

  const state = useMemo(
    () => deriveAttendanceClockState(rows, attendanceUserIds, sessionClock, carryoverRows),
    [attendanceUserIds, carryoverRows, rows, sessionClock],
  );

  useEffect(() => {
    if (loadedAttendanceUserId !== userId || !userId || error) return;

    const presenceKey = `${userId}:${state.active}`;
    if (lastPublishedPresenceRef.current === presenceKey) return;

    lastPublishedPresenceRef.current = presenceKey;
    void setAttendancePresence(state.active).catch(() => {
      if (lastPublishedPresenceRef.current === presenceKey) {
        lastPublishedPresenceRef.current = null;
      }
    });
  }, [attendanceRevision, error, loadedAttendanceUserId, state.active, userId]);

  return {
    ...state,
    busy,
    clock,
    error,
    loading: rowsLoading || modeLoading,
    requiresLocation: !isRemote && !isOvertimeActive,
    refresh,
    userId,
  };
}
